// src/components/LocationSelect.tsx: paired country / city selects.
// The city control becomes a free text input when the chosen country has no
// predefined list, or when the user picks "Other".
import React from 'react';
import { COUNTRIES, getCities } from '../lib/locations';

const C = {
  white: '#FFFFFF', border: '#D0D5DD', text: '#0A0A0A',
  muted: '#6B7280', navy: '#0A1628',
};

const baseInput: React.CSSProperties = {
  width: '100%', background: C.white,
  border: `1px solid ${C.border}`, borderRadius: '3px',
  color: C.text, fontSize: '13px',
  padding: '8px 10px', outline: 'none',
  fontFamily: 'system-ui', boxSizing: 'border-box',
};

const baseLabel: React.CSSProperties = {
  display: 'block', fontSize: '10px',
  fontWeight: 700, color: C.muted,
  marginBottom: '5px', letterSpacing: '0.6px',
  textTransform: 'uppercase', fontFamily: 'system-ui',
};

export interface LocationSelectProps {
  country: string;
  city: string;
  onCountryChange: (country: string) => void;
  onCityChange: (city: string) => void;
  required?: boolean;
  /** Show the field labels. Default true. */
  label?: boolean;
  countryLabel?: string;
  cityLabel?: string;
  /** Override the input styling so the pair matches the host form exactly. */
  inputStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  /** Lay the two fields out side by side. Default true. */
  columns?: boolean;
}

export default function LocationSelect({
  country, city, onCountryChange, onCityChange,
  required = false, label = true,
  countryLabel = 'Headquarters Country',
  cityLabel = 'Headquarters City',
  inputStyle, labelStyle, columns = true,
}: LocationSelectProps) {
  const input = { ...baseInput, ...inputStyle };
  const lbl = { ...baseLabel, ...labelStyle };
  const select = { ...input, cursor: 'pointer' as const };

  const cities = getCities(country);
  // Records saved before this list existed may hold a country that is no longer
  // an option. Surface it rather than silently showing an empty select.
  const countryOptions = country && !COUNTRIES.includes(country)
    ? [country, ...COUNTRIES]
    : COUNTRIES;

  // Free text once the country has no list, or the user chose "Other".
  const cityIsFreeText = cities.length === 0 || city === 'Other';
  const cityOptions = city && cities.length > 0 && !cities.includes(city) && city !== 'Other'
    ? [city, ...cities]
    : cities;

  const focus = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) =>
    (e.target.style.borderColor = C.navy);
  const blur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) =>
    (e.target.style.borderColor = C.border);

  return (
    <div style={columns
      ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }
      : undefined}>
      <div>
        {label && <label style={lbl}>{countryLabel}{required ? ' *' : ''}</label>}
        <select
          style={select}
          value={country}
          required={required}
          onChange={e => { onCountryChange(e.target.value); onCityChange(''); }}
          onFocus={focus}
          onBlur={blur}
        >
          <option value="">Select country...</option>
          {countryOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div>
        {label && <label style={lbl}>{cityLabel}{required ? ' *' : ''}</label>}
        {cityIsFreeText ? (
          <input
            style={input}
            type="text"
            value={city === 'Other' ? '' : city}
            required={required}
            placeholder="City"
            onChange={e => onCityChange(e.target.value)}
            onFocus={focus}
            onBlur={blur}
          />
        ) : (
          <select
            style={select}
            value={city}
            required={required}
            onChange={e => onCityChange(e.target.value)}
            onFocus={focus}
            onBlur={blur}
          >
            <option value="">Select city...</option>
            {cityOptions.map(c => <option key={c} value={c}>{c}</option>)}
            <option value="Other">Other</option>
          </select>
        )}
      </div>
    </div>
  );
}
